import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import { Button, Card, Checkbox, Col, Row, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams } from "react-router";
import { useGetVehicleMappingsByType } from "../../../../common/api/queries";
import { rowGutter } from "../../../../common/constants";
import { EntityIdObject } from "../../../../common/types";
import type { UUID } from "../../../../typings/global";
import VehicleBrandCreateMappingModal from "../components/brands/VehicleBrandCreateMappingModal";
import VehicleBrandMappingsListForm from "../components/brands/VehicleBrandMappingsListForm";
import { VehicleModelMappings } from "../components/models/VehicleModelMappings";
import { VehicleMappingActions } from "../components/VehicleMappingActions";
import { VehicleMappingType } from "../enums";
import { VEHICLE_BRAND_ROUTE_PATHS } from "../paths";

export const VehicleSettingsContainer = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { id } = useParams<EntityIdObject>();

  const [selectedBrandId, setSelectedBrandId] = useState<UUID | undefined>(id);
  const [isBrandCreateMappingModalOpen, setIsBrandCreateMappingModalOpen] = useState<boolean>(false);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState<boolean>(false);
  const [selectedMappingTypes, setSelectedMappingTypes] = useState<VehicleMappingType[]>(
    Object.values(VehicleMappingType),
  );

  const { data: vehicleMappingsData } = useGetVehicleMappingsByType(
    selectedMappingTypes,
    !!selectedMappingTypes.length && isDownloadEnabled,
  );

  const handleShowModelsClick = (brandMappingId: UUID) => {
    setSelectedBrandId(brandMappingId);
    navigate(generatePath(VEHICLE_BRAND_ROUTE_PATHS.models.to, { id: brandMappingId }));
  };

  const handleReturnToBrandsClick = () => {
    setSelectedBrandId(undefined);
    navigate(VEHICLE_BRAND_ROUTE_PATHS.brands.to);
  };

  return (
    <>
      <Card className="card-box" styles={{ body: { padding: "12px 24px" } }}>
        <h2 style={{ marginBottom: 0, fontSize: 24 }}>{t("vehicle.titles.pageTitle")}</h2>
      </Card>

      <Row gutter={rowGutter}>
        <Col xxl={18} xl={16} xs={24}>
          <Card
            className="card-box"
            title={t("vehicle.titles.selectedInstitutions")}
            styles={{ body: { minHeight: 160 } }}
            extra={
              <Space>
                <Button
                  size="small"
                  onClick={() => setSelectedMappingTypes([])}
                  icon={<AntIcon type="close" />}
                  color="danger"
                  variant="outlined"
                  disabled={selectedMappingTypes.length === 0}
                >
                  {t("vehicle.helpers.uncheckAll")}
                </Button>
                <Button
                  size="small"
                  onClick={() => setSelectedMappingTypes(Object.values(VehicleMappingType))}
                  icon={<AntIcon type="check" />}
                  color="green"
                  variant="outlined"
                  disabled={selectedMappingTypes.length === Object.values(VehicleMappingType).length}
                >
                  {t("vehicle.helpers.checkAll")}
                </Button>
              </Space>
            }
          >
            <Checkbox.Group onChange={setSelectedMappingTypes} value={selectedMappingTypes} style={{ width: "100%" }}>
              <Row gutter={[rowGutter, rowGutter]}>
                {Object.values(VehicleMappingType).map((type) => (
                  <Col xxl={4} xs={6} key={type}>
                    <Card size="small">
                      <Checkbox value={type}>{t("vehicle.enums.vehicleMappingType." + type)}</Checkbox>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Card>
        </Col>

        <Col xxl={6} xl={8} xs={24}>
          <Card
            className="card-box"
            title={t("vehicle.titles.vehicleMappingActions")}
            styles={{ body: { minHeight: 160 } }}
          >
            <VehicleMappingActions selectedMappingTypes={selectedMappingTypes} />
          </Card>
        </Col>
      </Row>

      {vehicleMappingsData?.length === 0 ? (
        <Button type="primary" icon={<AntIcon type="reload" />} onClick={() => setIsDownloadEnabled(true)}>
          {t("vehicle.actions.downloadMappings")}
        </Button>
      ) : selectedBrandId ? (
        <VehicleModelMappings
          selectedMappingTypes={selectedMappingTypes}
          selectedBrandId={selectedBrandId}
          onReturnsToBrand={handleReturnToBrandsClick}
          mappingData={vehicleMappingsData}
        />
      ) : (
        <Card
          className="card-box"
          title={<h3>{t("vehicle.titles.brandsMapping")}</h3>}
          extra={
            <SecondaryButton
              className="margin-top-tiny margin-bottom-tiny"
              icon={<AntIcon type="plus" />}
              onClick={() => setIsBrandCreateMappingModalOpen(true)}
            >
              {t("vehicle.actions.createBrand")}
            </SecondaryButton>
          }
        >
          <VehicleBrandMappingsListForm
            selectedMappingTypes={selectedMappingTypes}
            mappingData={vehicleMappingsData}
            onShowModelsClick={handleShowModelsClick}
          />

          <VehicleBrandCreateMappingModal
            open={isBrandCreateMappingModalOpen}
            selectedMappingTypes={selectedMappingTypes}
            mappingData={vehicleMappingsData}
            onFormCancel={() => setIsBrandCreateMappingModalOpen(false)}
          />
        </Card>
      )}
    </>
  );
};
